// src/pages/CartPage.js
import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { CartContext } from '../context/CartContext';
import './CartPage.css';
import InsuranceModal from "../pages/InsuranceModal";
import ReactMarkdown from 'react-markdown';

/**
 * Date-overlap check (unchanged).
 */
/*function rangesOverlap(startA, endA, startB, endB) {
    return startA <= endB && startB <= endA;
}*/

/*function hasOverlappingBookingsForSameItem(cart) {
    const itemsById = {};
    cart.forEach((ci) => {
        if (!itemsById[ci.itemID]) {
            itemsById[ci.itemID] = [];
        }
        itemsById[ci.itemID].push(ci);
    });

    for (const itemId in itemsById) {
        const bookings = itemsById[itemId];
        if (bookings.length > 1) {
            for (let i = 0; i < bookings.length; i++) {
                const aStart = new Date(bookings[i].startDate);
                const aEnd = new Date(bookings[i].endDate);
                for (let j = i + 1; j < bookings.length; j++) {
                    const bStart = new Date(bookings[j].startDate);
                    const bEnd = new Date(bookings[j].endDate);
                    if (rangesOverlap(aStart, aEnd, bStart, bEnd)) {
                        return true;
                    }
                }
            }
        }
    }
    return false;
}*/

/**
 * Helper to apply a discount object to a base price.
 */
function calcDiscountedPrice(basePrice, discount) {
    if (!discount) return basePrice;
    const discountAmount = parseFloat(discount.discountAmount) || 0;
    const discountPercent = parseFloat(discount.discountPercent) || 0;

    let price = basePrice;
    if (discountPercent > 0) {
        // e.g., 15% => multiply by (1 - 0.15) = 0.85
        price = price * (1 - discountPercent);
    } else if (discountAmount > 0) {
        price -= discountAmount;
        if (price < 0) price = 0;
    }
    return price;
}

function getPreauthorizationAmount(ci) {
    const amount = parseFloat(ci.depositAmt);
    return Number.isFinite(amount) && amount > 0 ? amount : 0;
}

function hasDuplicateRentalItems(cart) {
    const seenItemIds = new Set();

    for (const ci of cart) {
        if (!ci.itemID) continue;

        // Insurance is allowed as its own special line.
        if (ci.itemID === 24083) continue;

        if (seenItemIds.has(ci.itemID)) {
            return ci;
        }

        seenItemIds.add(ci.itemID);
    }

    return null;
}

function hasMixedRentalDates(cart) {
    const rentalItems = cart.filter(ci => {
        if (!ci.itemID) return false;

        // Ignore insurance.
        if (ci.itemID === 24083) return false;

        return true;
    });

    if (rentalItems.length <= 1) {
        return false;
    }

    const firstStartDate = rentalItems[0].startDate;
    const firstEndDate = rentalItems[0].endDate;

    return rentalItems.some(ci =>
        ci.startDate !== firstStartDate || ci.endDate !== firstEndDate
    );
}

/**
 * Logic for the nonrefundable reservation fee
 * (unchanged from your existing code, shown for completeness).
 */
function computeCartNonrefundableFee(cart, policy, appliedDiscount, userTaxRate) {
    if (!policy) return 0;
    if (!policy.useNonrefundableFee) {
        return 0;
    }

    // Compute the cart's grandTotal first
    let subtotalAfterDiscount = 0;
    for (const ci of cart) {
        const basePrice = parseFloat(ci.unitPrice);
        const discountedLinePrice = calcDiscountedPrice(basePrice, appliedDiscount);
        subtotalAfterDiscount += discountedLinePrice;
    }
    const taxAmount = subtotalAfterDiscount * userTaxRate;
    const grandTotal = subtotalAfterDiscount + taxAmount;

    const baseFee = policy.nonrefundableFeeAmount;
    if (!policy.useNonrefundableFeePerItem) {
        // Single flat fee
        let fee = baseFee;

        // Corner case: only 1 line with 1 qty => if discount < fee => fee=0
        if (cart.length === 1) {
            const singleItem = cart[0];
            const base = parseFloat(singleItem.unitPrice);
            const discounted = calcDiscountedPrice(base, appliedDiscount);
            const qty = parseInt(singleItem.unitQuantity, 10) || 1;

            if (qty === 1 && discounted < fee) {
                fee = 0;
            }
            if (grandTotal < fee) {
                fee = 0;
            }
        }
        return fee;
    }

    // Per-item logic
    let countEligibleLines = 0;
    for (const ci of cart) {
        // If item has ignoreNonRefundableReservationFee == true, skip it
        if (!ci.ignoreNonRefundableReservationFee) {
            countEligibleLines++;
        }
    }

    let fee = baseFee * countEligibleLines;
    // Corner case if there's only 1 line in the cart
    if (cart.length === 1) {
        const singleItem = cart[0];
        const base = parseFloat(singleItem.unitPrice);
        const discounted = calcDiscountedPrice(base, appliedDiscount);
        const qty = parseInt(singleItem.unitQuantity, 10) || 1;

        if (qty === 1 && discounted < fee) {
            fee = 0;
        }
        if (grandTotal < fee) {
            fee = 0;
        }
    }

    return fee;
}

const CartPage = () => {
    const navigate = useNavigate();
    const { cart, loadCart, removeFromCart } = useContext(CartContext);

    // Basic data & error handling
    const [userProfile, setUserProfile] = useState(null);
    const [userLoading, setUserLoading] = useState(true);
    const [cartLoading, setCartLoading] = useState(true);
    const [error, setError] = useState(null);

    // Discounts
    const [assignedDiscount, setAssignedDiscount] = useState(null);
    const [typedDiscountCode, setTypedDiscountCode] = useState('');
    const [overrideDiscount, setOverrideDiscount] = useState(null);

    // Taxes
    const [taxCategories, setTaxCategories] = useState([]);
    const [userTaxRate, setUserTaxRate] = useState(0.07);

    // User notes
    const [userNotes, setUserNotes] = useState('');

    // Rental policy (includes insurance %)
    const [policy, setPolicy] = useState(null);

    // ===== NEW: Track user’s desire to add insurance & the insurance line item =====
    const [includeInsurance, setIncludeInsurance] = useState(false);
    const [insuranceItem, setInsuranceItem] = useState(null);

    //Find and set Insurance Item for imageUrl
    const INSURANCE_ITEM_ID = 24083;
    const [insuranceProduct, setInsuranceProduct] = useState(null);

    // Insurance Modal
    const [showInsuranceModal, setShowInsuranceModal] = useState(false);

    // 0) Fetch Insurance Item once for ImageUrl
    // Fetch it once – you only need the imageUrl
    useEffect(() => {
        const loadInsuranceProduct = async () => {
            try {
                // Adjust the path if your controller is different
                const resp = await api.get(`/items/${INSURANCE_ITEM_ID}`);
                setInsuranceProduct(resp.data);          // { …, imageUrl: "https://…" }
            } catch (err) {
                console.error('Failed to load insurance product', err);
            }
        };
        loadInsuranceProduct();
    }, []);           // ← run once on mount

    // 1) Fetch user profile
    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const resp = await api.get('/profile');
                console.log("Profile response data = ", resp.data);
                setUserProfile(resp.data);

                // If user has an assigned discount
                if (resp.data.assignedDiscountId) {
                    try {
                        const discResp = await api.get('/lightspeed/discounts/search', {
                            params: { discountId: resp.data.assignedDiscountId },
                        });
                        setAssignedDiscount(discResp.data);
                    } catch (discountErr) {
                        console.error('Failed to fetch assigned discount:', discountErr);
                    }
                }
            } catch (err) {
                console.error('Failed to load user profile:', err);
                setError('Failed to load user profile. Please refresh the page.');
            } finally {
                setUserLoading(false);
            }
        };
        fetchUserProfile();
    }, []);

    // 2) Fetch cart items
    useEffect(() => {
        const fetchCartItems = async () => {
            try {
                const resp = await api.get('/cart');
                loadCart(resp.data);
            } catch (err) {
                console.error('Failed to load cart items:', err);
                setError('Failed to load cart items.');
            } finally {
                setCartLoading(false);
            }
        };
        fetchCartItems();
    }, [loadCart]);

    // 3) Fetch tax categories
    useEffect(() => {
        const fetchTaxCats = async () => {
            try {
                const resp = await api.get('/lightspeed/taxes');
                setTaxCategories(resp.data);
            } catch (err) {
                console.error('Failed to fetch tax categories', err);
            }
        };
        fetchTaxCats();
    }, []);

    // 4) Determine user’s tax rate
    useEffect(() => {
        if (userProfile && userProfile.assignedTaxCategoryId && taxCategories.length > 0) {
            const assignedId = parseInt(userProfile.assignedTaxCategoryId, 10);
            const found = taxCategories.find(tc => parseInt(tc.taxCategoryID, 10) === assignedId);
            setUserTaxRate(found ? found.tax1Rate : 0.07);
        }
    }, [userProfile, taxCategories]);

    // 5) Fetch the rental policy (contains insurance info)
    useEffect(() => {
        const fetchPolicy = async () => {
            try {
                const resp = await api.get('/store/rental-policy');
                setPolicy(resp.data);
            } catch (err) {
                console.error('Failed to load rental policy:', err);
            }
        };
        fetchPolicy();
    }, []);

    /**
     * 6) If policy supports rental insurance and the user has checked it,
     *    compute the insurance cost as a separate line item. If user
     *    unchecks or policy is disabled, remove it.
     */
    const appliedDiscount = overrideDiscount || assignedDiscount;

    useEffect(() => {
        if (!policy || !policy.useRentalInsurance) {
            setIncludeInsurance(false);
            setInsuranceItem(null);
            return;
        }

        if (!includeInsurance) {
            setInsuranceItem(null);
            return;
        }

        // If userProfile is not yet loaded or does not have a customerId, skip
        if (!userProfile || !userProfile.customerId) {
            console.warn("No userProfile or no customerId yet. Will try again once loaded.");
            return;
        }

        // ── Calculate subtotals for insurance ───────────────────────────────
        let subtotalBeforeDiscountForRealItems = 0;
        let subtotalAfterDiscountForRealItems  = 0;

        cart.forEach(ci => {
            const basePrice = parseFloat(ci.unitPrice);
            subtotalBeforeDiscountForRealItems += basePrice;

            const discounted = calcDiscountedPrice(basePrice, appliedDiscount);
            subtotalAfterDiscountForRealItems  += discounted;
        });

        // Decide which subtotal to use, based on the policy flag
        const baseSubtotalForInsurance = policy.chargeRentalInsuranceOffSubtotalBeforeDiscount
            ? subtotalBeforeDiscountForRealItems       // before discount
            : subtotalAfterDiscountForRealItems;       // after discount (legacy)

        const insuranceCost = baseSubtotalForInsurance *
            (policy.rentalInsurancePercentage / 100);

        const costFixed = parseFloat(insuranceCost.toFixed(2));

        const INSURANCE_ITEM_ID = 24083;

        const newItem = {
            id: 'insurance-line',
            customerId: userProfile.customerId,
            employeeID: 15,
            shopID: 1,
            itemID: INSURANCE_ITEM_ID,
            customSku: insuranceProduct?.customSku || '2/RENTALINS',
            itemDescription: insuranceProduct?.itemDescription || 'Rental Protection Coverage',
            unitPrice: costFixed,
            unitQuantity: 1,
            startDate: '2100-01-01',
            endDate: '2100-01-01',
            imageUrl: insuranceProduct?.imageUrl || null,
        };
        setInsuranceItem(newItem);

    }, [includeInsurance, policy, cart, appliedDiscount, userProfile, insuranceProduct,]);

    // Merge real cart items + insurance (if any)
    const finalCartItems = insuranceItem
        ? [...cart, insuranceItem]
        : cart;

    // 7) Handle discount code check
    const handleCheckDiscount = async () => {
        if (!typedDiscountCode) {
            setOverrideDiscount(null);
            return;
        }
        try {
            const resp = await api.get('/lightspeed/discounts/search', {
                params: { name: typedDiscountCode },
            });
            setOverrideDiscount(resp.data);
        } catch (err) {
            console.error('Failed to fetch override discount', err);
            setOverrideDiscount(null);
        }
    };

    // 8) Compute totals based on finalCartItems
    let subtotalBeforeDiscount = 0;
    let subtotalAfterDiscount = 0;
    finalCartItems.forEach(ci => {
        const base = parseFloat(ci.unitPrice);
        if (ci.id === 'insurance-line') {
            // Insurance is not discounted
            subtotalBeforeDiscount += base;
            subtotalAfterDiscount += base;
        } else {
            // Normal item
            subtotalBeforeDiscount += base;
            const discounted = calcDiscountedPrice(base, appliedDiscount);
            subtotalAfterDiscount += discounted;
        }
    });

    const rentalCartItems = cart; // real rental items only, not insurance

    const preauthHoldRequired = userProfile?.effectivePreauthHoldRequired !== false;

    const preauthorizationItems = preauthHoldRequired
        ? rentalCartItems
            .map(ci => ({
                id: ci.id,
                itemID: ci.itemID,
                itemDescription: ci.itemDescription || 'Rental Item',
                customSku: ci.customSku || '',
                depositAmt: getPreauthorizationAmount(ci),
            }))
            .filter(item => item.depositAmt > 0)
        : [];

    const preauthorizationTotal = preauthHoldRequired
        ? preauthorizationItems.reduce((sum, item) => sum + item.depositAmt, 0)
        : 0;

    const totalDiscount = subtotalBeforeDiscount - subtotalAfterDiscount;
    const taxAmount = subtotalAfterDiscount * userTaxRate;
    const grandTotal = subtotalAfterDiscount + taxAmount;

    // Nonrefundable reservation deposit (if used)
    const nonrefundableFee = computeCartNonrefundableFee(
        // IMPORTANT: pass only real items or pass finalCartItems?
        // Typically you'd pass real items, ignoring insurance line for the deposit.
        cart,
        policy,
        appliedDiscount,
        userTaxRate
    );

    // 9) Remove item from the cart
    const handleRemove = async (id) => {
        // If the user removes the insurance line explicitly
        if (id === 'insurance-line') {
            setIncludeInsurance(false);
            return;
        }
        // Otherwise remove a real item from the DB cart
        try {
            await api.delete(`/cart/${id}`);
            removeFromCart(id);
        } catch (err) {
            console.error('Failed to remove item:', err);
        }
    };

    // 10) Proceed to checkout
    const handleGoToCheckout = () => {
        // Validate no duplicate rental items in the same reservation.
        const duplicateItem = hasDuplicateRentalItems(cart);

        if (duplicateItem) {
            const itemName = duplicateItem.itemDescription || 'this item';

            alert(
                `You already have ${itemName} in your cart. ` +
                `To rent the same item for different dates, please create a separate reservation.`
            );

            return;
        }

        // Validate all real rental items have the same pickup and return dates.
        const mixedRentalDates = hasMixedRentalDates(cart);

        if (mixedRentalDates) {
            alert(
                'All rental items in one reservation must have the same pickup and return dates. ' +
                'Please create a separate reservation for items with different dates.'
            );

            return;
        }

        navigate('/checkout', {
            state: {
                notes: userNotes,
                subtotalBeforeDiscount,
                subtotalAfterDiscount,
                totalDiscount,
                taxAmount,
                grandTotal,
                userTaxRate,
                cart: finalCartItems,
                appliedDiscount,
                preauthorizationTotal,
                preauthorizationItems
            }
        });
    };

    // Show "Loading" or error if needed
    const overallLoading = userLoading || cartLoading;
    if (overallLoading) {
        return <p>Loading cart...</p>;
    }
    if (error) {
        return <p style={{ color: 'red' }}>{error}</p>;
    }
    if (!userProfile) {
        return <p style={{ color: 'red' }}>No user profile loaded.</p>;
    }

    // ── ReservationReminder Toast ─────────────────────────────────────────────
    function ReservationReminder() {
        const [open, setOpen] = useState(true);

        // auto‑hide after 15 sec
        useEffect(() => {
            const t = setTimeout(() => setOpen(false), 15000);
            return () => clearTimeout(t);
        }, []);

        if (!open) return null;

        return (
            <div className="toast-reminder">
                <button className="toast-close" onClick={() => setOpen(false)}>
                    &times;
                </button>
                <strong>Heads up!</strong> Reservations are <em>first‑come, first‑served.</em><br />
                Items remain available to others until you complete checkout.
            </div>
        );
    }

    // turn ON immediately after user accepts
    const confirmInsurance = () => {
        setIncludeInsurance(true);
        setShowInsuranceModal(false);
    };

// turn OFF when user cancels or un‑checks the box
    const cancelInsurance = () => {
        setIncludeInsurance(false);
        setShowInsuranceModal(false);
    };

    const handleInsuranceToggle = e => {
        if (e.target.checked) setShowInsuranceModal(true);
        else setIncludeInsurance(false);
    };

    function PreauthorizationHoldBox({ compact = false }) {
        if (preauthorizationTotal <= 0) return null;

        return (
            <div className={compact ? "preauth-box preauth-box-compact" : "preauth-box"}>
                <div className="preauth-header">
                    <div>
                        <h3>Preauthorization Hold</h3>
                        <p>
                            This is <strong>not charged at checkout</strong>. It is a temporary card hold
                            used only as security for rental equipment.
                        </p>
                    </div>
                    <div className="preauth-total">
                        ${preauthorizationTotal.toFixed(2)}
                    </div>
                </div>

                {!compact && (
                    <>
                        <div className="preauth-explanation">
                            You will not be charged this amount unless rental equipment is not returned,
                            is returned with severe damage, or is considered a total loss.
                        </div>

                        <div className="preauth-item-list">
                            {preauthorizationItems.map(item => (
                                <div className="preauth-item-row" key={`${item.itemID}-${item.id}`}>
                                <span>
                                    {item.itemDescription}
                                    {item.customSku && (
                                        <small> {item.customSku}</small>
                                    )}
                                </span>
                                    <strong>${item.depositAmt.toFixed(2)}</strong>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        );
    }

    // ============= Render the UI =============
    return (
        <div className="cart-container">
            <ReservationReminder />
            <h1>Your Cart</h1>

            {/* Discount Code Input */}
            <div className="discount-top-section">
                <div className="discount-section">
                    <label htmlFor="discountInput">Discount Code (Optional): </label>
                    <input
                        id="discountInput"
                        type="text"
                        value={typedDiscountCode}
                        onChange={(e) => setTypedDiscountCode(e.target.value)}
                        placeholder="Type discount name..."
                    />
                    <button onClick={handleCheckDiscount}>Check Discount</button>
                </div>

                {/* Display the applied discount */}
                {appliedDiscount && (
                    <div className="discount-applied">
                        <strong>Applied Discount:</strong> {appliedDiscount.name}
                        {appliedDiscount.discountPercent > 0
                            ? ` (${(appliedDiscount.discountPercent * 100).toFixed(0)}%)`
                            : appliedDiscount.discountAmount > 0
                                ? ` ($${appliedDiscount.discountAmount} off)`
                                : ''
                        }
                        <button
                            onClick={() => {
                                setTypedDiscountCode('');
                                setOverrideDiscount(null);
                                setAssignedDiscount(null);
                            }}
                        >
                            Remove Discount
                        </button>
                    </div>
                )}
            </div>

            {/* If the cart is empty, show message; else show items */}
            {finalCartItems.length === 0 ? (
                <p className="empty-cart">Your cart is empty.</p>
            ) : (
                <>
                    {/* Cart headers */}
                    <div className="cart-header-row">
                        <div className="cart-header-image">Image</div>
                        <div className="cart-header-description">Item</div>
                        <div className="cart-header-dates">Pickup Date</div>
                        <div className="cart-header-dates">Return Date</div>
                        <div className="cart-header-unitprice">Base Price</div>
                        <div className="cart-header-quantity">Qty</div>
                        <div className="cart-header-total">Discounted</div>
                        <div className="cart-header-action">Action</div>
                    </div>

                    {/* Cart items (real + insurance) */}
                    <div className="cart-items">
                        {finalCartItems.map(ci => {
                            // For insurance line, skip daily rate or dates
                            const isInsurance = ci.id === 'insurance-line';
                            if (isInsurance) {
                                return (
                                    <div className="cart-item-row" key={ci.id}>
                                        <img
                                            src={ci.imageUrl || '/placeholder-image.jpg'}
                                            alt="Insurance"
                                            className="cart-item-image"
                                        />
                                        <div className="cart-item-description">
                                            {ci.itemDescription}
                                        </div>
                                        <div className="cart-item-dates">—</div>
                                        <div className="cart-item-dates">—</div>
                                        <div className="cart-item-unit-price">
                                            ${ci.unitPrice.toFixed(2)}
                                        </div>
                                        <div className="cart-item-quantity">
                                            {ci.unitQuantity}
                                        </div>
                                        <div className="cart-item-total">
                                            ${ci.unitPrice.toFixed(2)}
                                        </div>
                                        <button
                                            className="cart-item-remove"
                                            onClick={() => handleRemove(ci.id)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                );
                            }

                            // Normal item
                            const basePriceForAllNights = parseFloat(ci.unitPrice);
                            const nights = parseInt(ci.unitQuantity, 10) || 1;
                            const discountedLinePrice = calcDiscountedPrice(basePriceForAllNights, appliedDiscount);
                            const discountedDailyRate = discountedLinePrice / nights;

                            return (
                                <div className="cart-item-row" key={ci.id}>
                                    <img
                                        src={ci.imageUrl || '/placeholder-image.jpg'}
                                        alt={ci.itemDescription || 'Item'}
                                        className="cart-item-image"
                                    />
                                    <div className="cart-item-description">
                                        {ci.itemDescription || 'No Description Available'}
                                    </div>
                                    <div className="cart-item-dates">{ci.startDate}</div>
                                    <div className="cart-item-dates">{ci.endDate}</div>
                                    <div className="cart-item-unit-price">
                                        ${basePriceForAllNights.toFixed(2)}
                                    </div>
                                    <div className="cart-item-quantity">
                                        {ci.unitQuantity}
                                    </div>
                                    <div className="cart-item-total">
                                        <div>${discountedLinePrice.toFixed(2)}</div>
                                        <div>
                                            <small>(${discountedDailyRate.toFixed(2)} / day × {nights} nights)</small>
                                        </div>
                                    </div>
                                    <button
                                        className="cart-item-remove"
                                        onClick={() => handleRemove(ci.id)}
                                    >
                                        Remove
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Preauthorization option A: directly below cart items */}
                    <PreauthorizationHoldBox />

                    {/* Let user choose whether to include Rental Insurance (if policy allows) */}
                    <div className="rental-insurance-line">
                        {/* Let user choose Rental Insurance (if policy allows) */}
                        {policy && policy.useRentalInsurance && (
                            <div
                                className="rental-insurance-line"
                                style={{
                                    margin: '1em 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '1rem'
                                }}
                            >
                                {/* checkbox + label */}
                                <label style={{ whiteSpace: 'nowrap' }}>
                                    <input
                                        type="checkbox"
                                        checked={includeInsurance}
                                        onChange={handleInsuranceToggle}
                                    />{' '}
                                    Add Rental Protection Coverage ({policy.rentalInsurancePercentage}%)
                                </label>

                                {showInsuranceModal && (
                                    <InsuranceModal
                                        deductiblePercent={policy.rentalCoverageDeductiblePercentage}
                                        deductible={policy.rentalInsuranceDeductible}
                                        coveragePercent={policy.rentalInsurancePercentage}
                                        description={policy.policyDescription && <ReactMarkdown>{policy.policyDescription}</ReactMarkdown>}
                                        onAccept={confirmInsurance}
                                        onCancel={cancelInsurance}
                                    />
                                )}

                            </div>
                        )}
                    </div>

                    <div className="cart-lower-section">
                        <div className="notes-and-preauth">
                            {/* Preauthorization option B: near notes/summary */}
                            <PreauthorizationHoldBox compact/>

                            {/* Notes box */}
                            <div className="notes-box">
                                <h5>Notes (Optional)</h5>
                                <textarea
                                    value={userNotes}
                                    onChange={(e) => setUserNotes(e.target.value)}
                                    rows={6}
                                    style={{width: '100%'}}
                                    placeholder="Any special requests or notes?"
                                />
                            </div>
                        </div>

                        {/* Summary totals */}
                        <div className="cart-summary">
                            <div className="cart-summary-row">
                                <span>Subtotal (before discount):</span>
                                <span>${subtotalBeforeDiscount.toFixed(2)}</span>
                            </div>
                            {totalDiscount > 0 && (
                                <div className="cart-summary-row">
                                    <span>Total Discount:</span>
                                    <span>-${totalDiscount.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="cart-summary-row">
                                <span>Subtotal (after discount):</span>
                                <span>${subtotalAfterDiscount.toFixed(2)}</span>
                            </div>
                            <div className="cart-summary-row">
                                <span>Tax ({(userTaxRate * 100).toFixed(2)}%):</span>
                                <span>${taxAmount.toFixed(2)}</span>
                            </div>
                            <div className="cart-summary-row cart-summary-total">
                                <span>Grand Total:</span>
                                <span>${grandTotal.toFixed(2)}</span>
                            </div>

                            {/* Nonrefundable Reservation Deposit */}
                            {policy && policy.useNonrefundableFee && (
                                <div
                                    className="cart-summary-row"
                                    style={{marginTop: '0.75em', fontWeight: 'bold'}}
                                >
                                    <span>Reservation Deposit:</span>
                                    <span>${nonrefundableFee.toFixed(2)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <button
                        className="confirm-booking-button"
                        onClick={handleGoToCheckout}
                    >
                        Checkout
                    </button>
                </>
            )}
        </div>
    );
};

export default CartPage;